"use client";
import React from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import {
  GraduationCap,
  Globe2,
  Users,
  Rocket,
  BookOpen,
  ShieldCheck,
  Medal,
  Award,
  CheckCircle2,
  ListChecks,
  ArrowRight,
} from "lucide-react";

// Standalone About Us page
// - No framer-motion
// - No shadcn/ui Card/Button imports
// - Pure React + Tailwind classes

export default function AboutUsStandalone() {
  const services = [
    { title: "Career Guidance", desc: "Personalized counselling to explore strengths and career paths.", icon: GraduationCap },
    { title: "University Selection", desc: "Helping students shortlist the best-fit universities.", icon: Globe2 },
    { title: "Visa Assistance", desc: "Guiding students through smooth visa processes.", icon: ShieldCheck },
    { title: "Scholarship Support", desc: "Finding financial aid opportunities tailored for you.", icon: Medal },
    { title: "Application Review", desc: "Ensuring polished SOPs, essays, and applications.", icon: BookOpen },
    { title: "Test Preparation", desc: "Helping students prepare for SAT, GRE, IELTS, TOEFL, and more.", icon: Rocket },
    { title: "SAS Certification (Authorized Partner)", desc: "Authorized training & exam prep with official SAS curriculum.", icon: Medal },
    { title: "Immigration Assistance", desc: "Comprehensive guidance for immigration and legal procedures.", icon: ShieldCheck },
    { title: "Alumni Network", desc: "Connecting students with successful alumni worldwide.", icon: Users },
    { title: "Post-Admission Support", desc: "Helping students adjust and succeed abroad.", icon: Globe2 },
  ];

  const leadershipTeam = [
    { name: "Dr. Sheelan Misra", role: "Founder & CEO", slug: "sheelan-misra" },
    { name: "Kavita Mallick", role: "Director Growth & Strategy", slug: "kavita-mallick" },
    { name: "Dr. Ranjith TP", role: "Psychometric assessment specialist | Certified Career Mentor", slug: "ranjith" },
    { name: "Ravikumar V R", role: "Founder President of Karnataka Management Association", slug: "ravikumar" },
    { name: "Palash Gupta", role: "Product R&D, Engineering, Strategy | GCC Leader", slug: "palash" },
  ];

  const advisoryBoard = [
    { name: "Dr. Ranjith TP", role: "Head of Talent Acquisition, Volvo Group India" },
    { name: "Ravikumar V R", role: "Founder President, Karnataka Management Association" },
    { name: "Palash Gupta", role: "Product R&D, Engineering Strategy | PMI Bangalore President" },
    { name: "Advisor 4", role: "Global Strategy & Education Expert" },
    { name: "Advisor 5", role: "Career Counsellor & Mentor" },
    { name: "Advisor 6", role: "International University Partnerships" },
    { name: "Advisor 7", role: "Innovation & Research Mentor" },
    { name: "Advisor 8", role: "Policy & Higher Education Specialist" },
    { name: "Advisor 9", role: "Corporate Training & Leadership Coach" },
    { name: "Advisor 10", role: "Industry Relations & Placements" },
  ];

  return (
    <div className="w-full flex flex-col items-center text-gray-900">
      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-16 -right-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-6xl px-6 py-24 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight drop-shadow-sm">
            About <span className="inline-block bg-white/10 rounded-xl px-3 py-1">UniThink</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base md:text-xl leading-relaxed text-white/90">
            Where boundless learning meets global opportunities.
          </p>
          {/* Partner badge */}
          <div className="mt-4 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-medium backdrop-blur border border-white/25">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 5l4 2-4 2-4-2 4-2zm-5 5l4 2v4l-4-2v-4zm6 6v-4l4-2v4l-4 2z"/></svg>
              SAS Authorized Partner
            </span>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <HashLink smooth to="/contact#contact-form">
            <button className="rounded-full px-6 py-5 text-base md:text-lg font-semibold bg-white text-blue-600 hover:bg-gray-100 shadow-xl">
              Book a Free Counselling Session
            </button>
            </HashLink>
            <a href="#services">
            <button className="rounded-full px-6 py-5 text-base md:text-lg bg-transparent border border-white/50 text-white hover:bg-white/10">
              Explore Services
            </button>
            </a>
          </div>

          {/* Quick stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[{ k: "Years Mentoring", v: "25+" }, { k: "Partner Universities", v: "1500+" }, { k: "Students Mentored", v: "2500+" }, { k: "Visa Acceptance", v: "98%" }].map((s) => (
              <div key={s.k} className="rounded-2xl bg-white/10 p-4 backdrop-blur border border-white/20">
                <div className="text-3xl md:text-4xl font-extrabold">{s.v}</div>
                <div className="text-xs md:text-sm text-white/90 mt-1">{s.k}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships strip */}
      <section className="w-full bg-white">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-center">
            <div className="text-center sm:text-left">
              <p className="text-xs uppercase tracking-widest text-gray-500">Accreditations</p>
              <p className="text-sm text-gray-700">Proudly recognized as best Emerging Education Services & Consulting Company of the year 2025.</p>
            </div>
            {/*<div className="col-span-2 flex flex-wrap items-center justify-center sm:justify-end gap-6">
              <img src="/partners/sas.svg" alt="SAS Authorized Partner" className="h-8 object-contain" loading="lazy" />
            </div>*/}
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="w-full">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/unithink-89630.appspot.com/o/awards%20icons%2Fnew%20award%202025%20cropped.JPG?alt=media&token=91ceda2d-9d0b-4bd9-85bc-abed101fd02e"
            alt="UniThink Team"
            className="rounded-2xl  shadow-2xl ring-1 ring-gray-200/70 hover:scale-[1.02] transition will-change-transform"
            loading="lazy"
          />
          <div className="ml-4">
            <h2 className="text-3xl md:text-4xl font-bold  tracking-tight mb-4 text-blue-800">Who We Are</h2>
            <p className="text-gray-700 text-base md:text-lg leading-relaxed space-y-2">
  <span className="block">
    <span className="font-semibold text-indigo-800 hover:text-indigo-800 transition">
      Unithink Education International Pvt. Ltd.
    </span>{" "}
    is your global mentor for education and career guidance.
  </span>
  <span className="block">
    With{" "}
    <span className="font-bold text-green-600 hover:text-green-800 transition">
      25+ years of experience
    </span>
    , we specialize in{" "}
    <span className="text-indigo-800 hover:underline cursor-pointer">university selection</span>,{" "}
    <span className="text-indigo-800 hover:underline cursor-pointer">admissions</span>,{" "}
    <span className="text-indigo-800 hover:underline cursor-pointer">visa support</span>,{" "}
    <span className="text-indigo-800 hover:underline cursor-pointer">scholarships</span>, and{" "}
    <span className="text-indigo-800 hover:underline cursor-pointer">immigration guidance</span>.
  </span>
  <span className="block">
    We proudly partner with{" "}
    <span className="font-bold text-pink-600 hover:text-pink-800 transition">
      1500+ universities worldwide
    </span>
    , serving students across the{" "}
    <span className="font-semibold">US, Canada, UK, Australia</span>, and 30+ more countries.
  </span>
</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <span className="rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-sm">Transparent Counselling</span>
              <span className="rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-sm">Scholarships & Visas</span>
              <span className="rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-sm">Career Mentoring</span>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="w-full bg-gray-50">
        <div className="mx-auto max-w-6xl px-6 py-16 grid md:grid-cols-2 gap-6 md:gap-10">
          {[{
            title: "Our Vision",
            body: "To serve as a lifelong education and career mentor, empowering students to achieve global excellence and guiding them through every stage of their academic and professional journey.",
          }, {
            title: "Our Mission",
            body: "To empower students worldwide with the tools, resources, and guidance they need for admissions, scholarships, visa support, and career growth—helping them confidently navigate the complexities of the education landscape.",
          }].map((b) => (
            <div key={b.title} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
              <h3 className="text-2xl md:text-3xl font-bold mb-3 text-blue-800">{b.title}</h3>
              <p className="text-gray-700 md:text-lg leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Leadership */}
      <section className="w-full">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-3xl md:text-4xl font-bold text-center tracking-tight mb-10">Leadership & Advisory Board</h2>
          <div className="flex flex-col gap-6">
  {/* First row with 2 members */}
  <div className="grid grid-cols-2 gap-6 w-3/4 mx-auto">
  {leadershipTeam.slice(0, 2).map((m) => (
    <div
      key={m.slug}
      className="rounded-2xl shadow-lg hover:shadow-xl transition border border-gray-100 overflow-hidden"
    >
      <div className="flex justify-center">
        <img
          src={`/team/${m.slug}.jpeg`}
          alt={m.name}
          className="h-52 w-52 object-fill transition"
          loading="lazy"
        />
      </div>
      <div className="p-4 text-center">
        <h4 className="text-base md:text-lg font-semibold tracking-tight">
          {m.name}
        </h4>
        <p className="text-blue-700 text-xs md:text-sm mt-1 font-medium">
          {m.role}
        </p>
      </div>
    </div>
  ))}
</div>


  {/* Second row with 3 members */}
  <div className="grid grid-cols-3 gap-6">
    {leadershipTeam.slice(2).map((m) => (
      <div
        key={m.slug}
        className="group rounded-2xl shadow-lg hover:shadow-2xl transition border border-gray-100 overflow-hidden"
      >
        <div className="flex justify-center">
          <img
            src={`/team/${m.slug}.jpeg`}
            alt={m.name}
            className="h-52 w-52 object-fill transition"
            loading="lazy"
          />
          
        </div>
        <div className="p-4 text-center">
          <h4 className="text-base md:text-lg font-semibold tracking-tight">
            {m.name}
          </h4>
          <p className="text-blue-700 text-xs md:text-sm mt-1 font-medium">
            {m.role}
          </p>
        </div>
      </div>
    ))}
  </div>
</div>

        </div>
      </section>

      {/* Services */}
      <section id='services' className="w-full bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-3xl md:text-4xl font-bold text-center tracking-tight mb-12 text-gray-800">Our Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.title} className="rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition bg-white">
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 rounded-xl p-3 bg-blue-50 text-blue-700">
                      {s.icon ? <s.icon className="h-6 w-6" /> : <Rocket className="h-6 w-6" />}
                    </div>
                    <div>
                      <h4 className="text-lg md:text-xl font-semibold mb-1 text-gray-900">{s.title}</h4>
                      <p className="text-gray-600 text-sm md:text-base">{s.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Authorizations (SAS) */}
      <section className="w-full bg-gray-50">
        <div className="mx-auto max-w-6xl px-6 py-16 grid md:grid-cols-2 gap-8 items-start">
          {/* Explainer + CTA */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 h-auto border border-gray-100">
            <h3 className="text-2xl md:text-3xl font-bold mb-3 text-blue-800">Certifications & Authorizations</h3>
            <p className="text-gray-700 md:text-lg leading-relaxed">
              As an <span className="font-semibold">SAS Authorized Partner</span>, we deliver industry-aligned training with verified curricula, hands-on labs, and exam preparation designed to improve career outcomes.
            </p>
            <p className="text-gray-700 md:text-lg leading-relaxed mt-3">
              Our certified mentors and structured pathways ensure learners gain practical, job-ready analytics skills recognized by global employers.
            </p>
            <div className="mt-6">
              <a href="/sas" className="inline-flex items-center gap-2 rounded-full bg-blue-800 hover:bg-blue-900 text-white px-5 py-3">
                See SAS Courses <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* 3‑Step SAS Flow */}
          <div className="grid gap-4">
            {[{ title: "Assess", desc: "Profile review + skill gap mapping to choose the right SAS track." },{ title: "Train", desc: "Instructor-led modules, labs, and projects aligned to SAS credentials." },{ title: "Certify", desc: "Official exam readiness, mock tests, and certification guidance." }].map((step, i) => (
              <div key={step.title} className="rounded-2xl border border-gray-100 shadow-sm">
                <div className="p-5 flex items-start gap-4">
                  <div className="shrink-0 rounded-xl p-3 bg-blue-50 text-blue-700">
                    {i === 0 && <ListChecks className="h-6 w-6" />}
                    {i === 1 && <BookOpen className="h-6 w-6" />}
                    {i === 2 && <CheckCircle2 className="h-6 w-6" />}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold">{i + 1}. {step.title}</h4>
                    <p className="text-gray-600 mt-1">{step.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SAS Testimonial 
        <div className="mx-auto max-w-6xl px-6 pb-4">
          <div className="rounded-2xl bg-white border border-gray-100 shadow-xl">
            <div className="p-6 md:p-8">
              <blockquote className="text-gray-800 text-base md:text-lg leading-relaxed">
                “UniThink’s SAS pathway turned my analytics fundamentals into hiring-ready skills. I cleared my certification on the first attempt and landed an analyst role within 6 weeks.”
              </blockquote>
              <div className="mt-4 text-sm text-gray-600">— Aditi Sharma, Data Analyst (SAS Certified)</div>
            </div>
          </div>
        </div>*/}
      </section>

      {/* Advisory Board 
      <section className="w-full bg-gray-50">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-3xl md:text-4xl font-bold text-center tracking-tight mb-10">Advisory Board</h2>
          <div className="grid xl:grid-cols-5 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-6">
            {advisoryBoard.map((a, i) => (
              <div key={`${a.name}-${i}`} className="rounded-2xl shadow-lg hover:shadow-2xl transition border border-gray-100 text-center overflow-hidden">
                <div className="pt-6 flex justify-center">
                  <img
                    src={`/advisors/advisor${i + 1}.jpg`}
                    alt={a.name}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover grayscale hover:grayscale-0 transition border-4 border-white shadow"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-base md:text-lg font-semibold text-red-600">{a.name}</h4>
                  <p className="text-gray-600 text-xs md:text-sm mt-2 leading-relaxed">{a.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>*/}

      {/* Awards & Recognitions 
      <section className="w-full bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-3xl md:text-4xl font-bold text-center tracking-tight mb-10">Awards & Recognitions</h2>
          <div className="grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-6">
            <div className="rounded-2xl border-2 border-amber-300 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
              <div className="p-6 flex items-start gap-4">
                <div className="shrink-0 rounded-xl p-3 bg-amber-50 text-amber-700"><Award className="h-6 w-6" /></div>
                <div>
                  <h4 className="text-lg font-semibold">Best Emerging Education Consultancy & Services Firm — 2025</h4>
                  <p className="text-gray-600 mt-1">Recognizing UniThink’s impact on student outcomes, partnerships, and innovation.</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100">
              <div className="p-6">
                <div className="flex items-center gap-3">
                  <img src="/awards/partner-sas.svg" alt="SAS Authorized Partner" className="h-8" loading="lazy" />
                  <div>
                    <div className="text-base font-semibold">SAS Authorized Partner</div>
                    <div className="text-gray-600 text-sm mt-1">Official training & certification partner</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100">
              <div className="p-6">
                <div className="flex items-center gap-3">
                  <img src="/awards/award-placeholder.svg" alt="Award Badge" className="h-8" loading="lazy" />
                  <div>
                    <div className="text-base font-semibold">Add Your Award</div>
                    <div className="text-gray-600 text-sm mt-1">Upload badges/logos as they come in</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>*/}

      {/* Contact */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-800 via-blue-700 to-blue-600" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-20 text-white text-center">
  <HashLink smooth to="/contact#contact-form">
  <button className="text-3xl md:text-4xl font-bold bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-2xl shadow-lg transition">
    Book a Free Counselling Session
  </button>
</HashLink>
  
          {/*<form className="mx-auto mt-8 max-w-3xl bg-white rounded-2xl p-6 md:p-8 shadow-2xl text-gray-800">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="sr-only" htmlFor="name">Name</label>
              <input id="name" type="text" placeholder="Name" className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-red-500/50" />
              <label className="sr-only" htmlFor="email">Email</label>
              <input id="email" type="email" placeholder="Email" className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-red-500/50" />
              <label className="sr-only" htmlFor="phone">Phone</label>
              <input id="phone" type="text" placeholder="Phone" className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-red-500/50" />
              <label className="sr-only" htmlFor="country">Country</label>
              <input id="country" type="text" placeholder="Country" className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-red-500/50" />
            </div>
            <label className="sr-only" htmlFor="service">Service</label>
            <select id="service" className="p-3 border rounded-lg w-full mt-4 focus:outline-none focus:ring-2 focus:ring-red-500/50">
              <option>Select Service Type</option>
              <option>Career Guidance</option>
              <option>Admissions</option>
              <option>Visa Support</option>
            </select>
            <button className="mt-6 w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-full text-base md:text-lg shadow-lg">
              Book Now
            </button>
          </form>*/}
        </div>
      </section>
    </div>
  );
}